import { useContext } from 'react';
import { Form } from 'react-bootstrap';
import { AnyColorLevel, ColorContrastStandard, colorLevelDisplayMap } from '../../../../types/color';
import { PaletteStateContext } from '../../../palette/contexts/PaletteStateContext';
import { ColorModeContext } from '../../contexts/ColorModeContext';
import './PreviewTextInput.css';

type PreviewTextInputProps = {
    color: {
        background: AnyColorLevel;
        text: AnyColorLevel;
        placeholder: AnyColorLevel;
        border?: AnyColorLevel;
    }
};

const textInputColorsByColorModeByStandard: Record<
    ColorContrastStandard,
    Record<'light' | 'dark', PreviewTextInputProps['color']>
> = {
    apca: {
        light: {
            background: 'APCA_WHITE',
            text: 'APCA_L900',
            placeholder: 'APCA_L450',
            border: 'APCA_L600',
        },
        dark: {
            background: 'APCA_BLACK',
            text: 'APCA_D900',
            placeholder: 'APCA_D450',
            border: 'APCA_D600',
        }
    },
    wcag21aaa: {
        light: {
            background: 'WCAG_2_1_AAA_WHITE',
            text: 'WCAG_2_1_AAA_BLACK',
            placeholder: 'WCAG_2_1_AAA_L500',
            border: 'WCAG_2_1_AAA_BLACK'
        },
        dark: {
            background: 'WCAG_2_1_AAA_BLACK',
            text: 'WCAG_2_1_AAA_WHITE',
            placeholder: 'WCAG_2_1_AAA_L300',
            border: 'WCAG_2_1_AAA_WHITE'
        }
    },
};

const INPUT_CSS_SELECTOR = 'PreviewTextInput--input';

const _getBorderCss = (selector: string, color: string) => `
.${selector}:focus {
  border-color: ${color};
}`;
const _getPlaceholderCss = (selector: string, color: string) => `
.${selector}::placeholder {
    color: ${color};
}`;

const PreviewTextInput = () => {
    const colorMode = useContext(ColorModeContext);
    const { palette, standard } = useContext(PaletteStateContext);
    const { text, placeholder, background, border } = textInputColorsByColorModeByStandard[standard][colorMode];
    const color = palette.colors[text];
    const placeholderCssSelectorValue = colorLevelDisplayMap[placeholder].cssSelector;
    const placeholderCssSelector = `${INPUT_CSS_SELECTOR}__placeholder-color--${placeholderCssSelectorValue}`;
    const borderCssSelectorValue = border ? colorLevelDisplayMap[border].cssSelector : 'none';
    const borderColor = border ? palette.colors[border] : undefined;
    const borderCssSelector = borderCssSelectorValue === 'none' ? '' : `${INPUT_CSS_SELECTOR}__border-color--${borderCssSelectorValue}`;
    let borderCss = '';
    if (borderColor && borderCssSelector) {
        borderCss = _getBorderCss(borderCssSelector, borderColor);
    }
    const placeholderLabel = colorLevelDisplayMap[placeholder].label;
    const placeholderColor = palette.colors[placeholder];
    const placeholderCss = _getPlaceholderCss(placeholderCssSelector, placeholderColor);
    const backgroundColor = palette.colors[background];
	const labelColor = colorMode === 'light' ? 'black' : 'white';
    return (
        <div className="PreviewTextInput">
			<div className="PreviewTextInput--label-group" style={{ color: labelColor }}>
				<p className="PreviewTextInput--label">
					{border
						? colorLevelDisplayMap[border].label
						: colorLevelDisplayMap[background].label}{' '}
					border
				</p>
				<p className="PreviewTextInput--label">
					{colorLevelDisplayMap[background].label} background
				</p>
				<p className="PreviewTextInput--label">
					{colorLevelDisplayMap[text].label} 16px Normal form input text
				</p>
			</div>
            <style>{`${placeholderCss}${borderCss}
`}</style>
            <Form.Control
                className={`
                    ${INPUT_CSS_SELECTOR}
                    ${placeholderCssSelector}
                    ${borderCssSelector}
                `}
                type="text"
                placeholder={`(${placeholderLabel}) 16px Normal placeholder text`}
                style={{
                    backgroundColor,
                    color,
                    borderColor
                }}
            />
        </div>
    );
};

export default PreviewTextInput;
